/**
 * PostHog Analytics Service
 * Tracks user behavior inside AIPE app and lead magnets
 */

import posthog from 'posthog-js';

// Initialize PostHog
const POSTHOG_KEY = 'phc_4lNxlo14egG9ActxlkYq5ADl2UcC9xkiGjpPThlMh5z';
const POSTHOG_HOST = 'https://us.i.posthog.com';

// Only track in production (not localhost)
const IS_PRODUCTION = !window.location.hostname.includes('localhost') &&
                      !window.location.hostname.includes('127.0.0.1');

let initialized = false;

const analyticsService = {
  /**
   * Initialize PostHog - call once on app start
   */
  init() {
    if (initialized) return;

    // Skip tracking in development
    if (!IS_PRODUCTION) {
      console.log('[Analytics] Disabled in development');
      initialized = true;
      return;
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture page leaves
      capture_pageleave: true,
      // Enable session recording (optional - can disable if not needed)
      disable_session_recording: false,
      // Respect Do Not Track
      respect_dnt: true,
      // Persistence
      persistence: 'localStorage',
      // Autocapture clicks, inputs, etc.
      autocapture: true,
    });

    initialized = true;
    console.log('[Analytics] PostHog initialized');
  },

  /**
   * Identify a user (call after login or when user info is available)
   * @param {string} distinctId - Unique user identifier (user_id or email)
   * @param {object} properties - User properties to set
   */
  identify(distinctId, properties = {}) {
    if (!initialized) return;

    if (!IS_PRODUCTION) {
      console.log('[Analytics DEV] User identified:', distinctId);
      return;
    }

    posthog.identify(distinctId, properties);
    console.log('[Analytics] User identified:', distinctId);
  },

  /**
   * Reset identity (call on logout)
   */
  reset() {
    if (!initialized) return;

    if (!IS_PRODUCTION) {
      console.log('[Analytics DEV] User reset');
      return;
    }

    posthog.reset();
    console.log('[Analytics] User reset');
  },

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {object} properties - Event properties
   */
  track(eventName, properties = {}) {
    if (!initialized) return;

    // In development, just log
    if (!IS_PRODUCTION) {
      console.log('[Analytics DEV] Event:', eventName, properties);
      return;
    }

    posthog.capture(eventName, properties);
    console.log('[Analytics] Event:', eventName, properties);
  },

  /**
   * Track page view (if not using automatic capture)
   * @param {string} pageName - Name of the page
   * @param {object} properties - Additional properties
   */
  pageView(pageName, properties = {}) {
    if (!initialized) return;

    posthog.capture('$pageview', {
      page_name: pageName,
      ...properties
    });
  },

  // ==========================================
  // LEAD MAGNET EVENTS
  // ==========================================

  /**
   * Track Mind to Method events
   */
  mind2method: {
    started(properties = {}) {
      analyticsService.track('m2m_started', {
        source: 'mind2method',
        ...properties
      });
    },

    stepCompleted(step, stepName, properties = {}) {
      analyticsService.track('m2m_step_completed', {
        source: 'mind2method',
        step_number: step,
        step_name: stepName,
        ...properties
      });
    },

    pathSelected(path, properties = {}) {
      analyticsService.track('m2m_path_selected', {
        source: 'mind2method',
        path: path, // 'have_ip' or 'explore'
        ...properties
      });
    },

    kgCheckViewed(properties = {}) {
      analyticsService.track('m2m_kg_check_viewed', {
        source: 'mind2method',
        ...properties
      });
    },

    completed(frameworkName, properties = {}) {
      analyticsService.track('m2m_completed', {
        source: 'mind2method',
        framework_name: frameworkName,
        ...properties
      });
    },

    pdfSent(properties = {}) {
      analyticsService.track('m2m_pdf_sent', {
        source: 'mind2method',
        ...properties
      });
    },

    videoWatched(properties = {}) {
      analyticsService.track('m2m_video_watched', {
        source: 'mind2method',
        ...properties
      });
    },

    abandoned(phase, properties = {}) {
      analyticsService.track('m2m_abandoned', {
        source: 'mind2method',
        phase: phase,
        ...properties
      });
    }
  },

  // ==========================================
  // TARGET MARKET DISCOVERY EVENTS
  // ==========================================

  targetMarket: {
    started(path, properties = {}) {
      analyticsService.track('tm_started', {
        source: 'target_market',
        path: path, // 'industry' or 'situation'
        ...properties
      });
    },

    stepCompleted(step, totalSteps, path, properties = {}) {
      analyticsService.track('tm_step_completed', {
        source: 'target_market',
        step: step,
        total_steps: totalSteps,
        path: path,
        ...properties
      });
    },

    segmentsGenerated(count, properties = {}) {
      analyticsService.track('tm_segments_generated', {
        source: 'target_market',
        segment_count: count,
        ...properties
      });
    },

    nichesGenerated(count, properties = {}) {
      analyticsService.track('tm_niches_generated', {
        source: 'target_market',
        niche_count: count,
        ...properties
      });
    },

    primarySelected(nicheName, properties = {}) {
      analyticsService.track('tm_primary_selected', {
        source: 'target_market',
        niche_name: nicheName,
        ...properties
      });
    },

    completed(properties = {}) {
      analyticsService.track('tm_completed', {
        source: 'target_market',
        ...properties
      });
    }
  },

  // ==========================================
  // COMMAND CENTER EVENTS
  // ==========================================

  commandCenter: {
    featureUsed(featureName, properties = {}) {
      analyticsService.track('cc_feature_used', {
        source: 'command_center',
        feature: featureName,
        ...properties
      });
    },

    onboardingStepCompleted(step, stepName, properties = {}) {
      analyticsService.track('cc_onboarding_step', {
        source: 'command_center',
        step: step,
        step_name: stepName,
        ...properties
      });
    },

    contentGenerated(contentType, properties = {}) {
      analyticsService.track('cc_content_generated', {
        source: 'command_center',
        content_type: contentType,
        ...properties
      });
    },

    moodBoardGenerated(archetype, properties = {}) {
      analyticsService.track('cc_mood_board_generated', {
        source: 'command_center',
        archetype: archetype,
        ...properties
      });
    }
  },

  // ==========================================
  // AUTH EVENTS
  // ==========================================

  auth: {
    login(method = 'email', properties = {}) {
      analyticsService.track('auth_login', {
        method: method,
        ...properties
      });
    },

    signup(source, properties = {}) {
      analyticsService.track('auth_signup', {
        source: source, // 'direct', 'quickstart', 'mind2method', etc.
        ...properties
      });
    },

    logout() {
      analyticsService.track('auth_logout');
      analyticsService.reset();
    }
  },

  // ==========================================
  // ERROR TRACKING
  // ==========================================

  error(errorType, errorMessage, properties = {}) {
    analyticsService.track('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...properties
    });
  }
};

export default analyticsService;
